/**
 * Cryptographic utilities for handling buffer conversions and Web Crypto API compatibility
 */

/**
 * Convert Uint8Array to BufferSource compatible format for Web Crypto API
 * This handles TypeScript compatibility issues with different buffer types
 */
export function toBufferSource(data: Uint8Array): BufferSource {
  // Create a new Uint8Array with proper ArrayBuffer to ensure compatibility
  return data as BufferSource;
}

/**
 * Ensure Uint8Array has proper ArrayBuffer type for Web Crypto API
 */
export function ensureArrayBuffer(data: Uint8Array): Uint8Array {
  if (data.buffer.constructor === ArrayBuffer) {
    return data;
  }
  // Create new Uint8Array with proper ArrayBuffer
  const newBuffer = new ArrayBuffer(data.byteLength);
  const newArray = new Uint8Array(newBuffer);
  newArray.set(data);
  return newArray;
}

/**
 * Convert string to Uint8Array
 */
export function stringToUint8Array(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

/**
 * Convert Uint8Array to string
 */
export function uint8ArrayToString(data: Uint8Array): string {
  return new TextDecoder().decode(data);
}

/**
 * Generate cryptographically secure random bytes
 */
export function randomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

/**
 * Secure array comparison (constant time)
 */
export function secureCompare(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false;
  }

  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a[i] ^ b[i];
  }

  return result === 0;
}

/**
 * Combine multiple Uint8Arrays
 */
export function concatArrays(...arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
  const result = new Uint8Array(totalLength);

  let offset = 0;
  for (const array of arrays) {
    result.set(array, offset);
    offset += array.length;
  }

  return result;
}